import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, CalendarDays, MapPin, Video, Quote } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { SM_ACCENT, SM_LOGO } from "@/data/content";
import { SMLeadForm } from "@/components/SMLeadForm";

const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

export default function SempliceMente() {
  const [percorsi, setPercorsi] = React.useState([]);
  const [areas, setAreas] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [testimonials, setTestimonials] = React.useState([]);
  const [area, setArea] = React.useState("all");
  const [format, setFormat] = React.useState("all");

  React.useEffect(() => {
    api.get("/sm/percorsi").then((r) => setPercorsi(r.data)).catch(() => {});
    api.get("/sm/areas").then((r) => setAreas(r.data)).catch(() => {});
    api.get("/sm/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/sm/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const areaMap = React.useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);
  const formats = React.useMemo(() => [...new Set(percorsi.map((p) => p.format).filter(Boolean))], [percorsi]);

  const filtered = percorsi.filter((p) => (area === "all" || p.area === area) && (format === "all" || p.format === format));

  return (
    <div className="bg-white">
      {/* Emotional hero */}
      <section className="relative overflow-hidden pt-16 pb-14 sm:pt-24 sm:pb-20" style={{ background: "linear-gradient(180deg,#faf7ff 0%,#ffffff 100%)" }}>
        <div className="absolute -top-32 -right-24 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(139,92,246,0.12)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <img src={SM_LOGO} alt="SempliceMente" className="h-16 sm:h-20 mx-auto mb-6 object-contain" data-testid="sm-logo" />
            <span className="inline-block text-xs font-700 uppercase tracking-widest mb-4" style={{ color: SM_ACCENT }}>Crescita · Persone · Benessere</span>
            <h1 className="font-display font-800 tracking-tighter text-3xl sm:text-5xl leading-tight max-w-3xl mx-auto" style={{ color: "#4c1d95" }}>
              Non solo sicurezza: persone che crescono
            </h1>
            <p className="mt-5 text-base sm:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
              SempliceMente è il progetto di Rischio Zero dedicato allo sviluppo delle competenze, alla crescita personale e al benessere organizzativo. Percorsi umani, concreti e su misura.
            </p>
            <a href="#percorsi" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-600 hover:scale-[1.03] transition-transform" style={{ background: `linear-gradient(135deg,#4c1d95 0%,${SM_ACCENT} 100%)` }} data-testid="sm-hero-cta">
              Esplora i percorsi <ArrowUpRight size={17} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Filters + percorsi */}
      <section id="percorsi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 -mt-2 scroll-mt-24">
        <div className="flex flex-wrap gap-2 mb-4" data-testid="sm-area-filters">
          <Chip active={area === "all"} onClick={() => setArea("all")} color={SM_ACCENT}>Tutte le aree</Chip>
          {areas.map((a) => (
            <Chip key={a.id} active={area === a.id} onClick={() => setArea(a.id)} color={a.color} testid={`sm-area-${a.id}`}>{a.name}</Chip>
          ))}
        </div>
        {formats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="sm-format-filters">
            <Chip small active={format === "all"} onClick={() => setFormat("all")} color="#6b7280">Tutti i formati</Chip>
            {formats.map((f) => (
              <Chip key={f} small active={format === f} onClick={() => setFormat(f)} color="#6b7280" testid={`sm-format-${f}`}>{f}</Chip>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="sm-grid">
          {filtered.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Sparkles;
            const ac = areaMap[c.area]?.color || SM_ACCENT;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}>
                <Link to={`/semplicemente/${c.slug}`} data-testid={`sm-card-${c.slug}`}
                  className="group block bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(76,29,149,0.12)] h-full">
                  <div className="relative h-44 overflow-hidden">
                    <img src={imgUrl(c.cover)} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(76,29,149,0) 40%,rgba(76,29,149,0.55) 100%)" }} />
                    <span className="absolute top-4 left-4 flex items-center justify-center w-10 h-10 rounded-2xl bg-white/90 backdrop-blur" style={{ color: ac }}><Icon size={20} /></span>
                    {c.format && <span className="absolute bottom-4 left-4 text-[11px] font-600 px-2.5 py-1 rounded-full bg-white/90 text-gray-700">{c.format}</span>}
                  </div>
                  <div className="p-6">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-700 uppercase tracking-wide" style={{ color: ac }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: ac }} />{areaMap[c.area]?.name || ""}
                    </span>
                    <h3 className="mt-1.5 font-display font-700 text-lg tracking-tight" style={{ color: "#3b0764" }}>{c.title}</h3>
                    <p className="mt-2 text-sm text-gray-500 line-clamp-2">{c.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-600 group-hover:gap-2.5 transition-all" style={{ color: SM_ACCENT }}>
                      Scopri il percorso <ArrowUpRight size={16} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
          {filtered.length === 0 && <p className="col-span-full text-center text-gray-400 py-16" data-testid="sm-empty">Nessun percorso per questo filtro.</p>}
        </div>
      </section>

      {/* Upcoming sessions */}
      {sessions.length > 0 && (
        <section className="bg-[#faf7ff] py-16 sm:py-20" data-testid="sm-sessions">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-xs font-700 uppercase tracking-widest mb-2" style={{ color: SM_ACCENT }}>Prossimi appuntamenti</span>
            <h2 className="font-display font-800 text-2xl sm:text-3xl tracking-tight" style={{ color: "#4c1d95" }}>Sessioni in programma</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {sessions.map((s) => {
                const d = (s.date || "").split("-");
                return (
                  <div key={s.id} data-testid={`sm-session-${s.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                    <div className="shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center text-white" style={{ background: `linear-gradient(135deg,#4c1d95,${SM_ACCENT})` }}>
                      <span className="font-800 text-lg leading-none">{d[2]}</span>
                      <span className="text-[10px] uppercase">{MONTHS[Number(d[1]) - 1]}</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-700 text-gray-900 text-sm leading-snug">{s.title}</h3>
                      <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1.5">{s.mode === "online" ? <Video size={13} /> : <MapPin size={13} />} {s.location}</p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5"><CalendarDays size={13} /> {s.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="sm-testimonials">
          <div className="text-center">
            <span className="inline-block text-xs font-700 uppercase tracking-widest mb-2" style={{ color: SM_ACCENT }}>Dicono di noi</span>
            <h2 className="font-display font-800 text-2xl sm:text-3xl tracking-tight" style={{ color: "#4c1d95" }}>Le voci di chi ha già scelto SempliceMente</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                data-testid={`sm-testimonial-${t.id}`} className="bg-white rounded-3xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(76,29,149,0.05)]">
                <Quote size={26} style={{ color: SM_ACCENT }} className="opacity-40" />
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{t.quote}</p>
                <div className="mt-5 flex items-center gap-3">
                  {t.avatar
                    ? <img src={imgUrl(t.avatar)} alt={t.author} className="w-11 h-11 rounded-full object-cover" />
                    : <span className="w-11 h-11 rounded-full flex items-center justify-center text-white font-700" style={{ background: SM_ACCENT }}>{t.author?.[0]}</span>}
                  <div>
                    <p className="font-700 text-gray-900 text-sm">{t.author}</p>
                    <p className="text-xs text-gray-400">{t.role}{t.company ? ` · ${t.company}` : ""}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Lead form */}
      <section id="richiedi" className="scroll-mt-24 pb-20 sm:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-[#8B5CF6]/15 overflow-hidden" style={{ background: "linear-gradient(180deg,#faf7ff 0%,#ffffff 100%)" }}>
            <div className="p-8 sm:p-10">
              <h2 className="font-display font-800 text-2xl sm:text-3xl tracking-tight text-center" style={{ color: "#4c1d95" }}>Richiedi un percorso su misura</h2>
              <p className="text-gray-500 mt-2 text-center max-w-lg mx-auto">Raccontaci le tue esigenze: costruiremo insieme il percorso più adatto alla tua azienda o a te.</p>
              <div className="mt-8"><SMLeadForm percorsi={percorsi} /></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Chip = ({ active, onClick, color, children, small, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full border font-600 transition-all hover:scale-[1.03] ${small ? "px-3.5 py-1.5 text-xs" : "px-4 py-2 text-sm"}`}
    style={active ? { background: color, color: "#fff", borderColor: color } : { background: `${color}12`, color, borderColor: "transparent" }}>
    {children}
  </button>
);
